import { useEffect, useState } from 'react'

/** Which columns are currently shown, keyed by column id. Missing keys default to visible. */
export type ColumnVisibilityState = Record<string, boolean>

const storageKeyFor = (id: string) => `osprey:column-visibility:${id}`

function readStored(storageKey: string): ColumnVisibilityState | null {
  try {
    const raw = localStorage.getItem(storageKeyFor(storageKey))
    return raw ? (JSON.parse(raw) as ColumnVisibilityState) : null
  } catch {
    // Private-mode/blocked storage, or corrupt JSON — fall back to defaults.
    return null
  }
}

function writeStored(storageKey: string, state: ColumnVisibilityState) {
  try {
    localStorage.setItem(storageKeyFor(storageKey), JSON.stringify(state))
  } catch {
    // Best-effort only — losing the preference is fine, it just won't persist.
  }
}

/**
 * PE-2254 — client-side, per-viewer column visibility for a data table. No backend
 * dependency: preferences are read/written straight to localStorage, namespaced by
 * `storageKey` so each table remembers its own set independently.
 *
 * `lockedIds` are always visible and never appear in the toggle list (e.g. a primary
 * identifier column, or a row-actions column that would otherwise leave a row with no
 * way to act on it).
 */
export function useColumnVisibility(storageKey: string, lockedIds: string[] = []) {
  const [visibility, setVisibility] = useState<ColumnVisibilityState>(
    () => readStored(storageKey) ?? {},
  )

  useEffect(() => {
    writeStored(storageKey, visibility)
    // storageKey intentionally omitted — a table's storageKey is static for its lifetime;
    // re-running this on a key change would overwrite a DIFFERENT table's preferences.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [visibility])

  const isVisible = (id: string) => lockedIds.includes(id) || visibility[id] !== false

  const toggle = (id: string) => {
    if (lockedIds.includes(id)) return
    setVisibility((v) => ({ ...v, [id]: !isVisible(id) }))
  }

  const reset = () => setVisibility({})

  // `visibility`/`setVisibility` are also handed back raw so a TanStack-table consumer
  // (see shared/components/Table.tsx) can wire them straight into `state.columnVisibility` /
  // `onColumnVisibilityChange` — the shapes are identical (missing/true = visible, false =
  // hidden), TanStack's own `enableHiding` already keeps a locked column from being toggled.
  return { isVisible, toggle, reset, visibility, setVisibility }
}
